/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `LogoProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface LogoProps {
    /**
     * A string representing the CSS class to be applied to the LogoIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A number or string representing the height of the LogoIcon element.
     *
     * @default 22
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Logo'
     */
    testId?: string;
    /**
     * A number or string representing the height of the LogoIcon element.
     *
     * @default 22
     */
    width?: number | string;
}

/**
 * The `Logo` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `LogoProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the LogoIcon element to override its default styling.
 * @param props.height    A number or string representing the height of the LogoIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the LogoIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const LogoComponent = <Logo className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Logo = ({className = '', height = 22, ref, testId = 'Logo', width = 22}: LogoProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 22 22"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M13.23 12.62q-.03-.64-.13-1.3a7 7 0 0 0-5.98-5.6A6.3 6.3 0 0 1 11.65.26c6.47-1.79 10.86 6.4 5.9 10.78a6.5 6.5 0 0 1-4.32 1.6" fill="currentColor" />
        <path d="M18.7 10.7c.17-.04 1.02.97 1.14 1.14a6.32 6.32 0 0 1-3.86 9.88 6.31 6.31 0 0 1-6.52-9.62c.11-.09.05-.05.11-.03.11.02.49.3.63.38a7 7 0 0 0 7.77-1c.12-.1.68-.74.73-.76" fill="currentColor" />
        <path d="M10.79 8.17q.54.52.91 1.16a7.2 7.2 0 0 0-3.26 3.4 7 7 0 0 0-.36 4.59c.1.4.3.78.4 1.19a6.3 6.3 0 0 1-8.4-5.02C-.67 7.46 6.5 3.87 10.8 8.17" fill="currentColor" />
    </svg>
);

Logo.displayName = 'Logo';